// frontend/src/pages/userProfileForm.js
//
// Turn a `/auth/user-profile/me/` payload into the account form UserProfile.jsx
// edits, and turn that form back into a PATCH body. Pure — no React — so the
// contract with the backend is unit-testable on its own (same shape as
// storeSettingsForms.js and itemSetupRow.js).
//
// ── Why the read is strict ───────────────────────────────────────────────
//
// Every field below is a NOT NULL column on `User` with a `''` default
// (authentication/models.py; first_name / last_name come from AbstractUser),
// and `UserProfileSerializer` declares each one `required=False,
// allow_blank=True`. So the payload always carries a string, and `''` is a
// real value meaning "not set" — for the locale axes it is what makes the
// effective value fall through to the Store.
//
// `email` is unique and not blank, so `''` is not a value it can hold. It is
// read strictly for the same reason — a missing one would seed the field with
// a blank that the next Save submits as an email change.
//
// A substituted default (`data.first_name || ''`) could only fire when the
// payload contract broke, and the section's own Save would then write the
// invented value over the real one — for a timezone, every timestamp the app
// renders for them would shift, silently. Throwing hands the page's existing
// catch a page-level error, the same treatment `storeSettingsForms.js` gives a
// malformed settings payload.

// The editable slice. `email` is edited like the rest but does NOT ride on
// every PATCH — see accountPatchBody.
export const ACCOUNT_FIELDS = [
  'email',
  'first_name', 'last_name', 'phone',
  'language', 'region_code', 'timezone',
];

// Read but not edited: `pending_email` drives the "confirmation sent" notice
// and the pending block. It is checked here so the whole read is one gate — a
// page that skipped it would render that notice about `undefined`.
const REQUIRED_STRINGS = [...ACCOUNT_FIELDS, 'pending_email'];

// The email notification switches (User.email_* columns). Read straight off
// the payload by the page's checkboxes, checked here for the same reason: a
// missing one would render a switch that is neither on nor off.
export const NOTIFICATION_FIELDS = ['email_stockout_alerts', 'email_promotion_updates'];

/**
 * @param {object} data  a `/auth/user-profile/me/` payload
 * @returns {object}  the account form, keyed by ACCOUNT_FIELDS
 * @throws {Error} when any required field is absent or has the wrong type
 */
export function extractAccountForm(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('User profile payload is missing');
  }
  const missing = [
    ...REQUIRED_STRINGS.filter((k) => typeof data[k] !== 'string'),
    ...NOTIFICATION_FIELDS.filter((k) => typeof data[k] !== 'boolean'),
  ];
  if (missing.length > 0) {
    throw new Error(`User profile payload is missing: ${missing.join(', ')}`);
  }
  return Object.fromEntries(ACCOUNT_FIELDS.map((k) => [k, data[k]]));
}

/**
 * The cached profile after an account Save: the response's account fields and
 * `pending_email` over `prev`, nothing else. The notification switches save on
 * their own PATCH, so this response may carry a switch value older than the
 * cache's.
 *
 * @param {object} prev  the cached `/auth/user-profile/me/` payload
 * @param {object} updated  the account Save's response
 * @returns {object}  the new cached payload
 */
export function mergeAccountSave(prev, updated) {
  return { ...prev, ...Object.fromEntries(REQUIRED_STRINGS.map((k) => [k, updated[k]])) };
}

/**
 * The PATCH body for one Save of the account form.
 *
 * `email` is the login credential, so submitting it does not write the column:
 * it parks the address in `pending_email` and mails a confirmation link there
 * (authentication/services/email_change.py). The backend refuses an address
 * that is already the account's own with `email_unchanged` — so a Save of the
 * phone number must not carry the untouched email along, or every such Save
 * would fail. It rides only when the merchant typed a different address.
 *
 * Whether that address is acceptable is the server's answer, not this
 * function's: a blank or malformed one is sent and refused out loud.
 *
 * @param {object} form  the account form, keyed by ACCOUNT_FIELDS
 * @param {string} currentEmail  the address the account signs in with today
 * @returns {object}  the PATCH body
 */
export function accountPatchBody(form, currentEmail) {
  const body = Object.fromEntries(
    ACCOUNT_FIELDS.filter((k) => k !== 'email').map((k) => [k, form[k]]),
  );
  const typed = form.email.trim();
  if (typed !== currentEmail) {
    body.email = typed;
  }
  return body;
}
